/**
 * useSettleAll — iter859 shared hook.
 *
 * Consolidates the previously duplicated "Settle All" logic across
 *   • pages/finance/SettleAllButton.jsx (period-scoped)
 *   • components/finance/MonthlyInterBranchView.jsx (month-end auto-settle)
 *
 * Handles the api.post call, toast messaging, and posting state so the
 * two UI wrappers only own their dialog layout + defaults. Fixes the
 * technical-debt item the user flagged in iteration 12.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * @param {object}   opts
 * @param {string}   opts.label       — noun used in toasts ("pair", "month")
 * @param {string}   opts.contextName — human label (e.g. "Mar 2026") for month-end
 * @param {(n:number)=>string} opts.fmtAmount — currency formatter
 * @param {Function} [opts.onDone]    — callback after success (typically refetch)
 */
export function useSettleAll({ label = 'pair', contextName = '', fmtAmount = (n) => String(n), onDone } = {}) {
  const [posting, setPosting] = useState(false);

  /**
   * @param {object} payload — { start_date, end_date, payment_mode, date, notes? }
   * @param {number} expectedCount — used to make the success toast readable
   */
  const settle = async (payload, expectedCount = 0) => {
    if (expectedCount === 0) {
      toast.info('Nothing to settle for the selected period.');
      return { ok: false, reason: 'empty' };
    }
    setPosting(true);
    try {
      const res = await api.post('/reports/inter-branch/settle-all', payload);
      const { settled = 0, failed = [], total_amount = 0 } = res.data || {};
      const suffix = settled === 1 ? '' : 's';
      const contextSuffix = contextName ? ` for ${contextName}` : '';
      if (failed.length === 0) {
        toast.success(`Settled ${settled} ${label}${suffix}${contextSuffix} — ${fmtAmount(total_amount)} total`);
      } else {
        toast.warning(`Settled ${settled}, ${failed.length} failed — review the list`);
      }
      if (typeof onDone === 'function') onDone();
      return { ok: true, settled, failed, total_amount };
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Settle-all failed';
      toast.error(detail);
      return { ok: false, error: detail };
    } finally {
      setPosting(false);
    }
  };

  return { settle, posting };
}
